import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import baseStyles from './css/BloodPressure.module.css';
import styles from './css/HealthRiskAssessment.module.css';
import { ClipboardPlus, Send } from 'lucide-react';

const HealthRiskAssessment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft, goToStep } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim() || "Thandiwe Ndlovu";
    const profileNumber = patient?.profilenumber || "448896";
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase() || "TN";

    // Lifestyle factors states
    const [smokingStatus, setSmokingStatus] = useState('');
    const [alcoholIntake, setAlcoholIntake] = useState('');
    const [exerciseFrequency, setExerciseFrequency] = useState('');
    
    // Body measurements states
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [bmi, setBmi] = useState('');
    
    // Other states
    const [clinicalNotes, setClinicalNotes] = useState('');

    // Calculate BMI when weight or height changes
    useEffect(() => {
        if (weight && height) {
            const weightInKg = parseFloat(weight);
            const heightInM = parseFloat(height) / 100; // Convert cm to m
            const calculatedBmi = (weightInKg / (heightInM * heightInM)).toFixed(1);
            setBmi(calculatedBmi);
        } else {
            setBmi('');
        }
    }, [weight, height]);

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            goToStep,
            serviceData: {
                health_risk_assessment: {
                    smokingStatus,
                    alcoholIntake,
                    exerciseFrequency,
                    weight,
                    height,
                    bmi,
                    clinicalNotes,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(baseStyles.hideSidebar);
        return () => {
            document.body.classList.remove(baseStyles.hideSidebar);
        };
    }, []);

    return (
        <div className={baseStyles.bloodPressureContainer}>
            <Navbar />

            <main className={baseStyles.mainContent}>
                <div className={baseStyles.card}>
                    <div className={baseStyles.cardHeader}>
                        <div className={baseStyles.headerIcon} aria-hidden="true">
                            <ClipboardPlus />
                        </div>
                        <h1 className={styles.title}>Health Risk Assessment</h1>
                    </div>

                    <div className={baseStyles.patientCard}>
                        <div className={baseStyles.avatar} aria-hidden="true">{initials}</div>
                        <div className={baseStyles.patientText}>
                            <div className={baseStyles.patientName}>{patientName}</div>
                            <div className={baseStyles.patientMetaRow}>
                                <span className={baseStyles.profileInfo}>Profile: {profileNumber}</span>
                                <span className={baseStyles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>LIFESTYLE FACTORS</div>
                        <div className={baseStyles.readingsRow1}>
                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Smoking Status</label>
                                <div className={baseStyles.inputGroup}>
                                    <Dropdown
                                        name="smokingStatus"
                                        value={smokingStatus}
                                        onChange={setSmokingStatus}
                                        options={['Non-smoker', 'Former smoker', 'Current smoker', 'Occasional smoker']}
                                        placeholder="Select..."
                                        className={baseStyles.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Alcohol Intake</label>
                                <div className={baseStyles.inputGroup}>
                                    <Dropdown
                                        name="alcoholIntake"
                                        value={alcoholIntake}
                                        onChange={setAlcoholIntake}
                                        options={['None', 'Occasional', 'Moderate', 'Heavy']}
                                        placeholder="Select..."
                                        className={baseStyles.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Exercise Frequency</label>
                                <div className={baseStyles.inputGroup}>
                                    <Dropdown
                                        name="exerciseFrequency"
                                        value={exerciseFrequency}
                                        onChange={setExerciseFrequency}
                                        options={['None', '1-2 times/week', '3-4 times/week', '5+ times/week']}
                                        placeholder="Select..."
                                        className={baseStyles.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.sectionTitle}>BODY MEASUREMENTS</div>
                        <div className={baseStyles.readingsRow}>
                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Weight</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="number"
                                        value={weight}
                                        onChange={(e) => setWeight(e.target.value)}
                                        className={baseStyles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={baseStyles.unit}>kg</span>
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>Height</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="number"
                                        value={height}
                                        onChange={(e) => setHeight(e.target.value)}
                                        className={baseStyles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={baseStyles.unit}>cm</span>
                                </div>
                            </div>

                            <div className={baseStyles.readingItem}>
                                <label className={baseStyles.readingLabel}>BMI (auto-calculated)</label>
                                <div className={baseStyles.inputGroup}>
                                    <input
                                        type="number"
                                        value={bmi}
                                        onChange={(e) => setBmi(e.target.value)}
                                        className={baseStyles.readingInput}
                                        placeholder='—'
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={baseStyles.sectionBlock}>
                        <div className={baseStyles.fieldLabel}>Clinical Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={baseStyles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={baseStyles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={baseStyles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default HealthRiskAssessment;